<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-50 p-6">
    <div class="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 space-y-6">
      <div class="text-center">
        <h2 class="text-2xl font-bold text-gray-800">
          Generate One-Time Auth. Code
        </h2>
        <p class="text-sm text-gray-500 mt-1">
          Generate a temporary registration code valid for your chosen duration.
        </p>
      </div>

      <form @submit.prevent="generateCode" class="space-y-4">
        <div>
          <label class="block text-gray-700 font-medium mb-1">Duration</label>
          <select
            v-model="selectedHours"
            class="w-full border border-gray-300 rounded-lg p-2 flex-1 focus-within:ring-2 focus-within:ring-ds-button-color"
            required
            :disabled="isActive"
          >
            <option disabled value="">Select duration</option>
            <option v-for="h in [1, 2, 3, 4, 5]" :key="h" :value="h">
              {{ h }} hour{{ h > 1 ? "s" : "" }}
            </option>
          </select>
        </div>
        <div>
          <label class="block text-gray-700 font-medium mb-1">
            Total Registrees
          </label>
          <input
            type="number"
            v-model="registreeCount"
            class="w-full border border-gray-300 rounded-lg p-2 flex-1 focus-within:ring-2 focus-within:ring-ds-button-color"
            placeholder="Enter total count"
            min="1"
            required
          />
        </div>
        <div v-if="!isActive" class="flex justify-center">
          <button
            type="submit"
            class="rounded-xl bg-ds-button-color px-5 py-2 font-semibold text-white shadow-md transition hover:scale-105 hover:bg-ds-button-color active:scale-95 text-center"
          >
            Generate Code
          </button>
        </div>
        <div v-else class="space-y-4 text-center">
          <div>
            <p class="text-gray-500">Generated Code:</p>
            <p class="text-2xl font-bold text-indigo-600">
              {{ generatedCode }}
            </p>
          </div>
          <p class="text-gray-600">
            Time Remaining:
            <span
              :class="[
                remainingTime === 'Expired'
                  ? 'text-red-500 font-semibold'
                  : 'text-green-600 font-semibold',
              ]"
            >
              {{ remainingTime }}
            </span>
          </p>
          <div class="flex justify-center gap-4">
            <button
              type="button"
              @click="updateCode"
              class="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 px-6 rounded-lg transition duration-300"
            >
              Update
            </button>
            <button
              type="button"
              @click="endSession"
              class="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-6 rounded-lg transition duration-300"
            >
              End Session
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, inject } from "vue";
import axios from "axios";

export default {
  setup() {
    const Swal = inject("$swal");

    const selectedHours = ref("");
    const registreeCount = ref("");
    const generatedCode = ref("");
    const isActive = ref(false);
    const expirationTime = ref("");
    const remainingTime = ref("");
    let timer = null;

    const resetCode = () => {
      clearInterval(timer);
      localStorage.removeItem("activeCode");
      isActive.value = false;
      generatedCode.value = "";
      expirationTime.value = "";
      remainingTime.value = "";
    };

    const startCountdown = (expiry) => {
      clearInterval(timer);
      timer = setInterval(() => {
        const diff = expiry - Date.now();
        if (diff <= 0) return resetCode();

        const h = Math.floor(diff / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        const s = Math.floor((diff % 60000) / 1000);
        remainingTime.value = `${h}h ${m}m ${s}s`;
      }, 1000);
    };

    const generateCode = async () => {
      try {
        const payload = {
          numberRegistration: registreeCount.value,
          hours: selectedHours.value,
        };
        const { data } = await axios.post("/wb/verification/otac", payload);

        if (data.status && data.data) {
          const expiry = Date.now() + selectedHours.value * 3600000;
          generatedCode.value = data.data.otac;
          expirationTime.value = expiry;
          isActive.value = true;

          localStorage.setItem(
            "activeCode",
            JSON.stringify({
              code: data.data.otac,
              expiry,
              registreeCount: registreeCount.value,
              selectedHours: selectedHours.value,
            })
          );

          startCountdown(expiry);

          await Swal.fire({
            title: "Code Generated!",
            text: `Your code "${data.data.otac}" will expire in ${
              selectedHours.value
            } hour${selectedHours.value > 1 ? "s" : ""}.`,
            icon: "success",
            confirmButtonColor: "#4f46e5",
          });
        } else {
          Swal.fire({
            title: "Error",
            text: data.message || "Failed to generate code.",
            icon: "error",
          });
        }
      } catch {
        Swal.fire({
          title: "Request Failed",
          text: "Unable to connect to the server. Please try again later.",
          icon: "error",
        });
      }
    };

    const updateCode = async () => {
      try {
        const { data } = await axios.put(
          `/wb/verification/otac/${generatedCode.value}`,
          { numberRegistration: registreeCount.value }
        );

        if (data.status === "true") {
          await Swal.fire({
            title: "Updated!",
            text: data.message,
            icon: "success",
            confirmButtonColor: "#4f46e5",
          });
        } else {
          Swal.fire({
            title: "Update Failed",
            text: data.message || "Unable to update code.",
            icon: "error",
          });
        }
      } catch {
        Swal.fire({
          title: "Request Failed",
          text: "Unable to connect to the server. Please try again later.",
          icon: "error",
        });
      }
    };

    const endSession = async () => {
      const result = await Swal.fire({
        title: "End Session?",
        text: "This will end your existing code session and stop the countdown.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#6b7280",
        confirmButtonText: "Yes, end it",
      });

      if (result.isConfirmed) {
        resetCode();
        Swal.fire({
          title: "Session Ended",
          text: "Your code has been cleared.",
          icon: "success",
          confirmButtonColor: "#4f46e5",
        });
      }
    };

    onMounted(() => {
      const saved = JSON.parse(localStorage.getItem("activeCode"));
      if (saved && saved.expiry > Date.now()) {
        generatedCode.value = saved.code;
        expirationTime.value = saved.expiry;
        registreeCount.value = saved.registreeCount;
        selectedHours.value = saved.selectedHours;
        isActive.value = true;
        startCountdown(saved.expiry);
      }
    });

    return {
      selectedHours,
      registreeCount,
      generatedCode,
      isActive,
      remainingTime,
      generateCode,
      updateCode,
      endSession,
    };
  },
};
</script>
